import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { getLiquidTemplateVariables } from "../utils/LiquidTemplate";
import { Box, IconButton, Menu, MenuItem, styled, Tooltip, Typography } from "@mui/material";
import { BorderedTextField, ColumnJustifyCenterFlex, RowJustifyStartFlex, StyledTextField } from "../commons/Containers";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Chip from "../commons/Chip";
import UploadDialog from "../commons/UploadDialog";
import AddUrl from "../commons/AddUrl";

const ExamplesGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 3fr',
    alignItems: 'center',
    justifyContent: 'start',
    padding: theme.spacing(1),
}));

const TextInput = ({ component, componentHandler, getTextNamedParams, addExample, maxVariables, editing }) => {

    useEffect(() => {
        if (!_.isUndefined(component.text)) {
            const prevVariables = !_.isEmpty(getTextNamedParams(component)) ? getTextNamedParams(component) : [];
            const currentVariables = getLiquidTemplateVariables(component.text);
            if (prevVariables.length !== currentVariables.length) {
                currentVariables.forEach(e => {
                    e.example = prevVariables.find(v => v.param_name === e.param_name)?.example;
                });
                addExample(component, currentVariables);
                componentHandler({ ...component });
            }
        }
    }, [component]);

    const exampleValueHandler = (list, key, value) => {
        const newList = _.clone(list);
        const param = newList.find(e => e.param_name === key);
        param.example = value;
        addExample(component, newList);
        componentHandler({ ...component });
    }

    return (
        <>
            <Typography variant="attribute" padding={1}>
                Content
            </Typography>
            <StyledTextField editing={editing} multiline maxRows={3} value={component.text ? component.text : ''} valueHandler={(value) => componentHandler && componentHandler({ ...component, text: value })} />
            {!_.isEmpty(getTextNamedParams(component)) && <><Typography variant="attribute" padding={1}>
                Examples
            </Typography>
                <ExamplesGrid key={component.type} gap={1} id={"examples-grid"}>
                    {getTextNamedParams(component) && getTextNamedParams(component).map((variable) => (
                        <>
                            <Typography key={variable.param_name} variant="body2" padding={1} alignSelf={'start'}>
                                {'{{' + variable.param_name + '}}'}
                            </Typography>
                            <BorderedTextField editing={editing} maxRows={1} value={variable.example} width="10ch" alignSelf={'start'} valueHandler={(value) => exampleValueHandler(getTextNamedParams(component), variable.param_name, value)} />
                        </>

                    ))}
                </ExamplesGrid></>}
        </>
    );

};

const getAttachmentResource = (comp, getAttachmentNameHandler) => {
    if (!_.isUndefined(comp.attachmentId)) {
        return getAttachmentNameHandler(comp.attachment_id);
    }
    if (!_.isUndefined(comp.external_url)) {
        return comp.externalUrl;
    }
    return null;
};

export const AbstractInput = ({ label = '', component, componentHandler }) => {

    const [uri, setUri] = useState(() => getAttachmentResource(component, (id => getAttachmentHandler(id)?.name)));
    const [addAnchor, setAddAnchor] = useState(null);
    const [uploadDialog, setUploadDialog] = useState(false);
    const [uploadedFile, setUploadedFile] = useState();
    const [addUrlDialog, setAddUrlDialog] = useState();
    const [link, setLink] = useState();

    useEffect(() => {
        if (uploadedFile) {
            setUri(uploadedFile.name);
            componentHandler({ ...component, uploaded_file: uploadedFile });
        }
    }, [uploadedFile]);

    useEffect(() => {
        if (link) {
            setUri(link);
            componentHandler({ ...component, external_url: link });
        }
    }, [link]);

    const getAttachmentHandler = (id) => {
        return { name: id };
    };

    const handleClose = () => {
        setAddAnchor(null);
    };

    const upload = () => {
        setUploadDialog(true);
        handleClose();
    };

    const addUrl = () => {
        setAddUrlDialog(true);
        handleClose();
    }

    const resetSelection = () => {
        setUri(null);
        setUploadedFile(null);
        setLink('');
    }

    return (
        <>
            <Typography variant="attribute" padding={1}>
                {label}
            </Typography>
            {_.isNull(uri) ? <>
                <RowJustifyStartFlex gap={1}>
                    <Typography>No {label.toLowerCase()} is added, add one</Typography>
                    <Tooltip title={`Add new ${label}`}>
                        <IconButton size="small" onClick={(e) => setAddAnchor(e.currentTarget)}>
                            <AddCircleOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </RowJustifyStartFlex>
                <Menu
                    id="basic-menu"
                    anchorEl={addAnchor}
                    open={Boolean(addAnchor)}
                    onClose={handleClose}
                    slotProps={{
                        list: {
                            'aria-labelledby': 'basic-button',
                        },
                    }}
                >
                    <MenuItem onClick={upload}>Upload new {label.toLowerCase()}</MenuItem>
                    <MenuItem onClick={handleClose}>Add {label.toLowerCase()} from Creative</MenuItem>
                    <MenuItem onClick={addUrl}>Add {label.toLowerCase()} from a link</MenuItem>
                </Menu>
            </> : <RowJustifyStartFlex><Chip label={uri} onDeleteHandler={resetSelection} /></RowJustifyStartFlex>}
            <UploadDialog label={`Upload ${label.toLowerCase()}`} open={uploadDialog} onClose={() => setUploadDialog(false)} onUpload={(file) => setUploadedFile(file)} />
            <AddUrl label={`Add ${label.toLowerCase()} url`} open={addUrlDialog} onClose={() => setAddUrlDialog(false)} onAdd={(url) => setLink(url)} />
        </>
    );

};

export const ImageInput = ({ component, componentHandler }) => AbstractInput({ label: 'Image', component: component, componentHandler: componentHandler });
export const VideoInput = ({ component, componentHandler }) => AbstractInput({ label: 'Video', component: component, componentHandler: componentHandler });
export const DocumentInput = ({ component, componentHandler }) => AbstractInput({ label: 'Document', component: component, componentHandler: componentHandler });

export const LocationInput = ({ component, componentHandler }) => {

    const [canAutoDetect] = useState(() => 'geolocation' in navigator);
    const [autoDetectError, setAutoDetectError] = useState('');

    const getLocationValue = (key, comp) => {
        const value = comp.location?.[key];
        return value ? value : '';
    };

    const handleLocationValue = (key, value, comp, compHandler) => {
        if (_.isEmpty(comp.location)) {
            comp.location = {};
        }
        compHandler({ ...comp, location: { ...comp.location, [key]: value } });
    };

    const autoDetectLocation = () => {
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition((position) => {
                componentHandler({ ...component, location: { ...component.location, latitude: position.coords.latitude, longitude: position.coords.longitude } });
                setAutoDetectError('');
            }, (error) => setAutoDetectError(error.message));
        } else {
            setAutoDetectError('The device doesn\'t support location settings, or please allow location access');
        }
    };

    const AutoDetectButton = ({ autoDetectLocation, error }) => {
        const sx = { color: !_.isEmpty(error) ? 'red' : null };
        return (
            <Tooltip title={error}>
                <IconButton color="success" size="small" onClick={autoDetectLocation} sx={{ ...sx }}>
                    <Typography variant="body1" >
                        Autodetect
                    </Typography>
                    <LocationOnIcon fontSize='small' />
                </IconButton>
            </Tooltip>
        );
    }

    return (
        <>
            <ColumnJustifyCenterFlex>
                <Typography variant="body2">
                    Location
                </Typography>
                {canAutoDetect && <AutoDetectButton autoDetectLocation={autoDetectLocation} error={autoDetectError} />}
            </ColumnJustifyCenterFlex>
            <ExamplesGrid>
                <Typography variant="body2">
                    Latitude
                </Typography>
                <StyledTextField width="10ch" value={getLocationValue('latitude', component)} valueHandler={(value) => handleLocationValue('latitude', value, component, componentHandler)} />
                <Typography variant="body2">
                    Longitude
                </Typography>
                <StyledTextField width="10ch" value={getLocationValue('longitude', component)} valueHandler={(value) => handleLocationValue('longitude', value, component, componentHandler)} />
                <Typography variant="body2">
                    Name
                </Typography>
                <StyledTextField width="10ch" value={getLocationValue('name', component)} valueHandler={(value) => handleLocationValue('name', value, component, componentHandler)} />
                <Typography variant="body2">
                    Address
                </Typography>
                <StyledTextField multiline width="15ch" value={getLocationValue('address', component)} valueHandler={(value) => handleLocationValue('address', value, component, componentHandler)} />
            </ExamplesGrid>
        </>
    );
}

export const InputFactory = {
    'text': TextInput,
    'image': ImageInput,
    'video': VideoInput,
    'document': DocumentInput,
    'location': LocationInput,
};